import useForkRef from "@rooks/use-fork-ref";
import React from "react";
import clsx from "clsx";
import { useMeasure } from "react-use";
import * as classes from "./Advert.module.css";
import { ShopifyAdvert, ShopifyMedia } from "./ProductListingPage";

const ImageAsset = ({
  creative,
}: {
  creative: ShopifyMedia & { media_type: "image" };
}) => {
  const [measureRef, measureRect] = useMeasure();

  const widths = [320, 480, 640];
  const src = creative.value;
  const srcSet = React.useMemo(
    () =>
      widths
        .map((width, _, arr) => `${src}?width${width} ${width / arr[0]}x`)
        .join(", "),
    [src]
  );

  return (
    <div
      ref={measureRef}
      className="media"
      style={{ paddingBottom: `${Math.floor(100 / creative.aspect_ratio)}%` }}
      // style={{ height: `100%` }}
    >
      <img
        alt={creative.alt}
        width={320}
        height={Math.ceil(320 / creative.aspect_ratio)}
        src={src}
        srcSet={srcSet}
        loading="lazy"
        sizes={measureRect.width + "px"}
      />
    </div>
  );
};

const VideoAsset = ({
  creative,
}: {
  creative: ShopifyMedia & { media_type: "video" };
}) => {
  const [measureRef, measureRect] = useMeasure();
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const combinedRef = useForkRef(measureRef, videoRef);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          const video = entry.target as HTMLVideoElement;
          video.load();
          video.play();
          observer.unobserve(video);
        }
      },
      { rootMargin: "400px 0px" }
    );
 
    observer.observe(videoRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const targetSource = React.useMemo(() => {
    const mpegSources = creative.value.sources.filter(
      ({ format }) => format === "mp4"
    );

    const source = creative.value.sources.find(
      ({ width }) => width >= measureRect.width * window.devicePixelRatio
    );

    return source || mpegSources[mpegSources.length - 1];
  }, [measureRect.width]);

  return (
    <div
      className="media"
      style={{ paddingBottom: `${100 / creative.aspect_ratio}%` }}
      // style={{ height: `100%` }}
    >
      <video
        ref={combinedRef}
        poster={creative.preview_image}
        preload="metadata"
        disablePictureInPicture
        disableRemotePlayback
        loop
        autoPlay
        muted
        playsInline
      >
        <source src={targetSource.url} type={targetSource.mime_type} />
      </video>
    </div>
  );
};


export const Advert = ({
  advert,
  slot,
}: {
  advert: ShopifyAdvert;
  slot: number;
}) => {
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          ecommerce: null,
        });
        window.dataLayer.push({
          event: "promotionView",
          ecommerce: {
            promoView: {
              promotions: [
                {
                  id: advert.campaign_id,
                  name: advert.campaign_name,
                  creative: advert.creative,
                  asset_link: typeof advert.creative.value !== "string" ? advert.creative.value.sources[0].url : advert.creative.value,
                  position: slot,
                },
              ],
            },
          },
        });
        observer.disconnect();
      }
    });

    observer.observe(rootRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      ecommerce: null,
    });

    const navigate = () => {
      window.location.href = href;
    };

    window.dataLayer.push({
      event: "promotionClick",
      ecommerce: {
        promoClick: {
          promotions: [
            {
              name: advert.campaign_name,
              id: advert.campaign_id,
              creative: advert.creative,
              asset_link: typeof advert.creative.value !== "string" ? advert.creative.value.sources[0].url : advert.creative.value,
              position: slot,
            },
          ],
        },
      },
      eventCallback: navigate,
      eventTimeout: 1000, // 1s
    });

    setTimeout(navigate, 1000);
  };

  if (advert.link != null) {
    var href = advert.link;
    var target =
        new URL(href).origin !== window.location.origin ? "_blank" : "_self";
  }
   
  return (
    <article className={classes.root} ref={rootRef}>
      <a
        onClick={handleClick}
        className={clsx('link-unstyled', {
          pointer_events_none: advert.link == null,
        })}
        href={href}
        target={target}
      >
        {advert.creative.media_type === "video" ? (
          <VideoAsset creative={advert.creative} />
        ) : (
          <ImageAsset creative={advert.creative} />
        )}
      </a>
    </article>
  );
};
